import { addDays } from 'date-fns';
import { filterFreeSlots, studioDayKey, type BookingCollection, type SlotBooking } from '@atink/domain';
import type { ArtistAvailability, DayOfWeek, Studio } from '@/domain/types';

/** Tattoos need roughly two weeks to heal before the next sitting. */
export const HEALING_MIN_DAYS = 14;

export type SlotBand = { key: '2wk' | '3wk' | '4wk'; weeks: 2 | 3 | 4; slots: string[] };

/**
 * Healing starts from the sitting, so the window anchors on the session date,
 * but never before "now" (the sim clock) for records seeded in the past.
 */
export function healingBaseIso(sessionStartIso: string, clockIso: string): string {
  return new Date(sessionStartIso).getTime() >= new Date(clockIso).getTime()
    ? sessionStartIso
    : clockIso;
}

/** Earliest a rebook may be booked: base + 14 days. */
export function rebookMinIso(sessionStartIso: string, clockIso: string): string {
  return addDays(new Date(healingBaseIso(sessionStartIso, clockIso)), HEALING_MIN_DAYS).toISOString();
}

/**
 * What the picker must avoid: the artist the next session is with, how long it
 * runs, and every booking we know of (any shape the DB rule reads). With it the
 * picker only offers slots the DB double-book guard will accept (tv-2na.8.5) —
 * the same `filterFreeSlots` rule as `bookings_artist_no_double_book`.
 */
export interface HealingSlotBusy {
  artistId: string;
  durationMin: number;
  bookings: BookingCollection<SlotBooking>;
  /** A booking to ignore (the DB never compares a row with itself). */
  excludeBookingId?: string;
}

/**
 * When the studio is open and the artist works (tv-2na.10.8). With it the picker
 * only offers a start on a day the studio is open and the artist is rostered
 * (not an RDO / leave day), inside both windows. Either half may be absent.
 */
export interface HealingSlotHours {
  studioHours?: Studio['operatingHours'];
  availability?: Pick<ArtistAvailability, 'pattern' | 'rdoDates' | 'leaveBlocks'>;
}

const DOW: DayOfWeek[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

function minutesOf(hhmm: string): number {
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
  return (h ?? 0) * 60 + (m ?? 0);
}

function ymdLocal(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function isOpenStart(iso: string, hours: HealingSlotHours): boolean {
  const d = new Date(iso);
  const dow = DOW[d.getDay()]!;
  const at = d.getHours() * 60 + d.getMinutes();
  const inside = (from: string, to: string) => at >= minutesOf(from) && at < minutesOf(to);
  // A record with no hours on file (a partial / legacy row) doesn't restrict.
  if (hours.studioHours && Object.keys(hours.studioHours).length > 0) {
    const slot = hours.studioHours[dow];
    if (!slot || !inside(slot.open, slot.close)) return false;
  }
  const av = hours.availability;
  if (av?.pattern && Object.keys(av.pattern).length > 0) {
    const win = av.pattern[dow];
    if (!win || !inside(win.startTime, win.endTime)) return false;
  }
  const day = ymdLocal(d);
  // Seeded dates may carry a time (`2026-05-25T00:00:00+10:00`) and a calendar-made
  // day off is a UTC instant of studio midnight (the previous UTC date): compare
  // studio days, never the string's first ten characters (B7, 8uf3).
  const studioDay = (s: string) => (/^\d{4}-\d{2}-\d{2}$/.test(s) ? s : studioDayKey(s));
  if (av?.rdoDates?.some((d) => studioDay(d) === day)) return false;
  if (av?.leaveBlocks?.some((lb) => day >= studioDay(lb.startDate) && day <= studioDay(lb.endDate))) {
    return false;
  }
  return true;
}

type BandDef = {
  key: SlotBand['key'];
  weeks: 2 | 3 | 4;
  pairs: Array<{ offset: number; hour: number }>;
};

const BAND_DEFS: BandDef[] = [
  { key: '2wk', weeks: 2, pairs: [{ offset: 14, hour: 11 }, { offset: 16, hour: 14 }] },
  { key: '3wk', weeks: 3, pairs: [{ offset: 21, hour: 11 }, { offset: 23, hour: 15 }] },
  { key: '4wk', weeks: 4, pairs: [{ offset: 28, hour: 11 }, { offset: 30, hour: 13 }] },
];

/** Fallback times tried (in order) within a band's week when a default is busy. */
const FALLBACK_HOURS = [11, 14];

function slotAt(base: Date, offset: number, hour: number): string {
  const day = addDays(base, offset);
  day.setHours(hour, 0, 0, 0);
  return day.toISOString();
}

/**
 * Six suggested slots in three week-bands (a couple each at 2, 3 and 4 weeks).
 * Deterministic from the base date (no Date.now / Math.random): fixed day
 * offsets (all >= 14) and fixed times, two distinct options per band.
 *
 * With `busy`, a default that clashes with a firm booking for the artist is
 * swapped for the next free time in the same week (fixed order, still
 * deterministic), so the picker never offers a slot the write would 409 on.
 */
export function generateHealingSlots(
  sessionStartIso: string,
  clockIso: string,
  busy?: HealingSlotBusy,
  hours?: HealingSlotHours,
): SlotBand[] {
  const base = new Date(healingBaseIso(sessionStartIso, clockIso));
  return BAND_DEFS.map(({ key, weeks, pairs }) => {
    const defaults = pairs.map(({ offset, hour }) => slotAt(base, offset, hour));
    if (!busy && !hours) return { key, weeks, slots: defaults };

    const fallbacks: string[] = [];
    for (let d = 0; d < 7; d += 1) {
      for (const hour of FALLBACK_HOURS) fallbacks.push(slotAt(base, weeks * 7 + d, hour));
    }
    const open = [...new Set([...defaults, ...fallbacks])].filter(
      (startIso) => !hours || isOpenStart(startIso, hours),
    );
    const free = busy
      ? filterFreeSlots(
          open.map((startIso) => ({
            startIso,
            endIso: new Date(Date.parse(startIso) + busy.durationMin * 60_000).toISOString(),
            artistId: busy.artistId,
            excludeBookingId: busy.excludeBookingId,
          })),
          busy.bookings,
        ).map((c) => c.startIso)
      : open;
    return {
      key,
      weeks,
      slots: free.slice(0, pairs.length).sort((a, b) => Date.parse(a) - Date.parse(b)),
    };
  });
}
